import Polygon from '@arcgis/core/geometry/Polygon';
import Polyline from '@arcgis/core/geometry/Polyline';
import Point from '@arcgis/core/geometry/Point';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine.js';
import { geojsonToArcGIS } from '@terraformer/arcgis';

const TOLERANCE = 0.0000002;

const numbersAreSameWithinTolerance = (n1: number, n2: number) => {
  const roundedN1 = Number(n1.toFixed(7));
  const roundedN2 = Number(n2.toFixed(7));

  return Math.abs(roundedN1 - roundedN2) < TOLERANCE;
};

const coordinatesSameWithinTolerances = (c1: any[], c2: any[]) => {
  if (c1.length !== c2.length || typeof c1[0] !== typeof c2[0]) {
    return false;
  }

  if (JSON.stringify(c1) === JSON.stringify(c2)) {
    return true;
  }

  if (typeof c1[0] === 'number') {
    return numbersAreSameWithinTolerance(c1[0], c2[0])
      && numbersAreSameWithinTolerance(c1[1], c2[1]);
  }

  for (let x = 0; x < c1.length; x++) {
    if (!coordinatesSameWithinTolerances(c1[x], c2[x])) {
      return false;
    }
  }

  return true;
};

export const geometriesMatch = (geom1, geom2) => {
  if ((!geom1 && !geom2) || JSON.stringify(geom1) === JSON.stringify(geom2)) {
    return true;
  }

  if (geom1?.type !== geom2?.type) {
    return false;
  }

  return coordinatesSameWithinTolerances(geom1.coordinates, geom2.coordinates);
};

export function calculatePointAlongPolyline(polyline, targetDistance) {
  let cumulativeLength = 0;

  for (let i = 0; i < polyline.paths[0].length - 1; i++) {
    const startPoint = polyline.getPoint(0, i);
    const endPoint = polyline.getPoint(0, i + 1);
    const segment = new Polyline({
      paths: [[[startPoint.x, startPoint.y], [endPoint.x, endPoint.y]]],
      spatialReference: polyline.spatialReference,
    });

    const segmentLength = geometryEngine.geodesicLength(segment, 'kilometers');
    cumulativeLength += segmentLength;

    if (cumulativeLength >= targetDistance) {
      const remainingLength = targetDistance - (cumulativeLength - segmentLength);
      const ratio = remainingLength / segmentLength;

      const interpolatedX = startPoint.x + (endPoint.x - startPoint.x) * ratio;
      const interpolatedY = startPoint.y + (endPoint.y - startPoint.y) * ratio;

      return new Point({
        x: interpolatedX,
        y: interpolatedY,
        spatialReference: polyline.spatialReference,
      });
    }
  }

  return polyline.getPoint(0, polyline.paths[0].length - 1);
}

export const numberOfPoints = (geometry) => {
  if (geometry.type === 'Polygon') {
    return `Polygon: ${geometry.coordinates[0].length - 1} points`;
  }
  if (geometry.type === 'MultiPolygon') {
    const numOfArrays = geometry.coordinates.length;

    return `MultiPolygon: ${(geometry.coordinates.flat(Infinity).length / 2) - numOfArrays} points`;
  }

  return `${geometry.type}: ${geometry.coordinates.length} points`;
};

export const calculatePerimeterOrLength = (geometry) => {
  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const polygon = new Polygon(geojsonToArcGIS(geometry));
    const perimeterInMeters = geometryEngine.geodesicLength(polygon, 'meters');
    const perimeterInFeet = geometryEngine.geodesicLength(polygon, 'feet');
    return `Perimeter: ${perimeterInFeet.toFixed(2)} ft (${perimeterInMeters.toFixed(2)} m)`;
  }

  if (geometry.type === 'LineString' || geometry.type === 'MultiLineString') {
    const line = new Polyline(geojsonToArcGIS(geometry));
    const lengthInFeet = geometryEngine.geodesicLength(line, 'feet');
    const lengthInMeters = geometryEngine.geodesicLength(line, 'meters');
    return `Length: ${lengthInFeet.toFixed(2)} ft (${lengthInMeters.toFixed(2)} m)`;
  }

  return '';
};

export const calculateArea = (geometry) => {
  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    const polygon = new Polygon(geojsonToArcGIS(geometry));
    const areaInSquareMeters = geometryEngine.geodesicArea(polygon, 'square-meters');
    const areaInSquareFeet = geometryEngine.geodesicArea(polygon, 'square-feet');
    return `Area: ${areaInSquareFeet.toFixed(2)} ft² (${areaInSquareMeters.toFixed(2)} m²)`;
  }

  return '';
};
